import { defaultSections } from "@reactive-resume/schema";
import { RichInput } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import { AiActions } from "@/client/components/ai-actions";
import { useResumeStore } from "@/client/stores/resume";

import { CoachSectionBadge } from "../../../_components/coach/coach-section-badge";
import {
  CoachSectionCollapseToggle,
  useCoachSectionBody,
} from "../../../_components/coach/coach-section-collapse";
import { CoachSectionSuggestions } from "../../../_components/coach/coach-section-suggestions";
import { getSectionIcon } from "./shared/section-icon";
import { SectionOptions } from "./shared/section-options";

export const SummarySection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore(
    (state) => state.resume.data.sections.summary ?? defaultSections.summary,
  );
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";
  const { visible: bodyVisible, collapsible: inCoachSession } = useCoachSectionBody("summary");

  return (
    <section id="summary" className="grid gap-y-6">
      <header className="flex items-center justify-between gap-x-3">
        <div className="flex min-w-0 items-center gap-x-4">
          {/* See SectionBase's header for why `leading-none` is what aligns the icon and badge with
              the title: `items-center` otherwise centres them on the 2.25rem line box rather than on
              the glyphs inside it. Coach-only. */}
          <span className="flex shrink-0 items-center">{getSectionIcon("summary")}</span>
          <h2 className={cn("line-clamp-1 text-3xl font-bold", inCoachSession && "leading-none")}>
            {section.name}
          </h2>
        </div>

        <div className="flex items-center gap-x-2">
          <CoachSectionBadge sectionId="summary" />
          {/* Both render null outside a coach session. */}
          <CoachSectionCollapseToggle sectionId="summary" />
          <SectionOptions id="summary" />
        </div>
      </header>

      {/* Collapsed by default during a coach optimization only; `bodyVisible` is always true in the
          normal builder. The suggestion card is inside the gate with the editor it rewrites, so a
          collapsed section is just its header and its "AI Suggested (n)" count. */}
      {bodyVisible && (
        <>
          {/* The summary rewrite, directly above the editor it replaces. */}
          <CoachSectionSuggestions sectionId="summary" />

          <main className={cn(!section.visible && "opacity-50")}>
            <RichInput
              content={section.content}
              onChange={(value) => setValue("sections.summary.content", value)}
              boldOnly={isMastersUnion}
              freezeItalic={isMastersUnion}
              {...(isMastersUnion && { maxLength: 345 })}
              {...(!isMastersUnion && {
                footer: (editor) => (
                  <AiActions
                    value={editor.getText()}
                    content={editor.getHTML()}
                    onChange={(value) => {
                      editor.commands.setContent(value);
                      setValue("sections.summary.content", value);
                    }}
                  />
                ),
              })}
            />
          </main>
        </>
      )}
    </section>
  );
};
